import { Annotations, Datum, Shape } from 'plotly.js'
import polygonClipping, { type Polygon as Poly } from 'polygon-clipping'
import { CellInfoDimension, PinnedMorphotype } from 'redux/slices'
import { approxEquals } from 'utils/helpers'
import { CellInfoWithFilterFlag } from '../tsv/types'
import { LabelFont } from '../shared'
import { checkForOverlappingMorphotypes, ensurePathClosed } from '../utils'
import calculateClusterOutline from './calculateClusterOutline'

const { union } = polygonClipping

type Point2D = { x: number; y: number }
const toNumber = (d: Datum | undefined) => +(d ?? -Infinity)

const getCoordsFromShape = (s: Partial<Shape>) => {
  const { x0, x1, y0, y1 } = {
    x0: toNumber(s.x0),
    x1: toNumber(s.x1),
    y0: toNumber(s.y0),
    y1: toNumber(s.y1),
  }

  return { x0, x1, y0, y1 }
}

/*
  @TODO: Having the higlighting logic in here causes all the polygon
  logic to run just to set fillcolor, opacity, and font. Let's see if we can
  move it somewhere else.
*/
const getShapeStandardProps = (pcg: PinnedMorphotype, isOverlapping = false) => {
  let fillcolor: string | undefined
  let opacity: number | undefined

  if (isOverlapping) {
    fillcolor = '#ffcdd2'  // Light red
    opacity = 0.15
  } else if (pcg.isHighlighted) {
    fillcolor = '#bbdefb'  // Light blue
    opacity = 0.1
  } else {
    fillcolor = 'transparent'  
    opacity = 1
  }

  return {
    xref: 'x',
    yref: 'y',
    fillcolor,
    opacity,
  } as Partial<Shape>
}

const getAnnotationStandardProps = (pcg: PinnedMorphotype) =>
  ({
    text: pcg.name,
    font: {
      ...LabelFont,
      size: pcg.isHighlighted ? LabelFont.size + 2 : LabelFont.size,
    },
    opacity: pcg.isHighlighted ? 1 : 0.8,
  } as Partial<Annotations>)

/**
 * Gets highest point from an SVG path.  This function is exported mainly for unit testing only.
 * @param path SVG path string
 * @returns highest point coordinate in 2D
 */
export const getHighestPointFromPath = (path?: string): Point2D => {
  let highestCoord: Point2D = { x: 0, y: Number.NEGATIVE_INFINITY }
  let secondHighestCoord: Point2D = { x: 0, y: Number.NEGATIVE_INFINITY }

  if (!path) return highestCoord

  const points = path
    .replace(/(M|Z)/g, '')
    .split('L')
    .map((coordinatePairAsString) =>
      coordinatePairAsString.split(',').map((coordinate) => +coordinate)
    )

  points?.forEach((point) => {
    /*
          using >= here instead of > because the points are
          stored counterclockwise and we want to use the leftmost coordinate
        */
    if (point[1] >= highestCoord.y) {
      secondHighestCoord = highestCoord
      highestCoord = { x: point[0], y: point[1] }
    }
  })

  if (approxEquals(highestCoord.y, secondHighestCoord.y)) {
    // the top is a flat horizontal line and we want the x coord to be in the middle
    highestCoord = { x: (highestCoord.x + secondHighestCoord.x) / 2, y: highestCoord.y }
  }

  return highestCoord
}

/**
 * Returns the highest point from a list of shapes (rectangles or polygons)
 * @param shapesInput An array of shapes or a single shape
 * @returns The highest point across all
 */
export const getHighestPointFromShapes = (
  shapesInput: Partial<Shape>[] | Partial<Shape>
): Point2D => {
  let highestCoord: Point2D = { x: 0, y: Number.NEGATIVE_INFINITY }

  const shapes = Array.isArray(shapesInput) ? shapesInput : [shapesInput]

  shapes.forEach((shape) => {
    if (shape.path) {
      // it's a polygon
      const highestFromPath = getHighestPointFromPath(shape.path)
      if (highestFromPath.y > highestCoord.y) {
        highestCoord = highestFromPath
      }
    } else {
      // it's a box
      const { x0, x1, y0, y1 } = getCoordsFromShape(shape)

      const x = (x0 + x1) / 2
      const y = Math.max(y0, y1)
      if (y > highestCoord.y) highestCoord = { x, y }
    }
  })
  return highestCoord
}
/**
 *
 * @param pcg
 * @returns
 */
const getPolygonFromPinnedMorphotype = (pcg: PinnedMorphotype): Poly => {
  // older sessions' version config won't have transposed cells so use regular cells
  const { selections, lassoPoints, range } = pcg.transposedCells ?? pcg.cells ?? {}

  let poly: Poly = [[[0, 0]]]

  if (lassoPoints) {
    poly = [lassoPoints.x.map((lassoPointX, i) => [lassoPointX, lassoPoints.y[i]])]
  } else if (range) {
    // Handle box selection from range format: { x: [min, max], y: [min, max] }
    const [x0, x1] = range.x
    const [y0, y1] = range.y

    // creating a box counterclockwise from lower left point
    poly = [
      [
        [x0, y0],
        [x1, y0],
        [x1, y1],
        [x0, y1],
        [x0, y0],
      ],
    ]
  } else if (selections) {
    const s = selections[0]
    const { x0, x1, y0, y1 } = getCoordsFromShape(s)

    // creating a box counterclockwise from lower left point
    poly = [
      [
        [x0, y0],
        [x1, y0],
        [x1, y1],
        [x0, y1],
        [x0, y0],
      ],
    ]
  }

  return poly
}

// Constants
const TILE_OFFSET_MULTIPLIER = 1.5

// Helper function to translate SVG path coordinates by adding offsets
const translatePathCoordinates = (pathString: string, xOffset: number, yOffset: number): string => {
  return pathString.replace(/([ML])([^ML]*)/g, (_match, command, coords) => {
    const numbers = coords.trim().split(/[,\s]+/).filter(Boolean)
    let result = command
    for (let i = 0; i < numbers.length; i += 2) {
      if (i + 1 < numbers.length) {
        const x = parseFloat(numbers[i]) + xOffset
        const y = parseFloat(numbers[i + 1]) + yOffset
        result += `${x},${y}`
        if (i + 2 < numbers.length) result += ' '
      }
    }
    return result
  })
}

// Helper function to detect which tile a selection was created on
const detectSourceTile = (shape: Partial<Shape>, bounds: { minX: number; minY: number; maxX: number; maxY: number }): { col: number; row: number } => {
  const xOffset = (bounds.maxX - bounds.minX) * TILE_OFFSET_MULTIPLIER
  const yOffset = (bounds.maxY - bounds.minY) * TILE_OFFSET_MULTIPLIER
  
  let centerX = 0
  let centerY = 0
  
  if (shape.type === 'rect' || shape.type === 'circle') {
    if (shape.x0 !== undefined && shape.x1 !== undefined) {
      centerX = ((shape.x0 as number) + (shape.x1 as number)) / 2
    }
    if (shape.y0 !== undefined && shape.y1 !== undefined) {
      centerY = ((shape.y0 as number) + (shape.y1 as number)) / 2
    }
  } else if (shape.type === 'path' && shape.path) {
    const pathString = shape.path as string
    const coords = pathString.match(/[\d.-]+/g)
    if (coords && coords.length >= 2) {
      let sumX = 0
      let sumY = 0
      let count = 0
      for (let i = 1; i < coords.length; i += 2) {
        sumX += parseFloat(coords[i - 1])
        sumY += parseFloat(coords[i])
        count += 1
      }
      centerX = sumX / count
      centerY = sumY / count
    }
  }
  
  // Determine which tile this center point belongs to
  const col = Math.max(0, Math.floor((centerX - bounds.minX) / xOffset))
  const row = Math.max(0, Math.floor((bounds.maxY - centerY) / yOffset))
  
  return { col, row }
}

// Helper function to transform shape coordinates
const transformShapeCoordinates = (shape: Partial<Shape>, colOffset: number, rowOffset: number): Partial<Shape> => {
  const transformedShape = { ...shape }
  
  if (transformedShape.type === 'rect' || transformedShape.type === 'circle') {
    if (transformedShape.x0 !== undefined) {
      transformedShape.x0 = (transformedShape.x0 as number) + colOffset
    }
    if (transformedShape.x1 !== undefined) {
      transformedShape.x1 = (transformedShape.x1 as number) + colOffset
    }
    if (transformedShape.y0 !== undefined) {
      transformedShape.y0 = (transformedShape.y0 as number) + rowOffset
    }
    if (transformedShape.y1 !== undefined) {
      transformedShape.y1 = (transformedShape.y1 as number) + rowOffset
    }
  } else if (transformedShape.type === 'path' && transformedShape.path) {
    const pathString = transformedShape.path as string
    const transformedPath = translatePathCoordinates(pathString, colOffset, rowOffset)
    transformedShape.path = ensurePathClosed(transformedPath)
  }
  
  return transformedShape
}

// Helper function to replicate shapes and annotations across tiles
const replicateAcrossTiles = (
  shapesToUse: Partial<Shape>[],
  highestPoints: Point2D[],
  pinnedMorphotype: PinnedMorphotype,
  visibleDimensions: CellInfoDimension[],
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  sourceTile: { col: number; row: number },
  focusedTile: { col: number; row: number } | undefined
) => {
  const { minX, minY, maxX, maxY } = bounds
  const xOffset = (maxX - minX) * TILE_OFFSET_MULTIPLIER
  const yOffset = (maxY - minY) * TILE_OFFSET_MULTIPLIER
  
  const replicatedShapes: Partial<Shape>[] = []
  const replicatedAnnotations: Partial<Annotations>[] = []
  
  // Get the number of columns and rows from comparison dimensions
  const numColumns = visibleDimensions[0]?.values?.length || 1
  const numRows = visibleDimensions[1]?.values?.length || 1
  
  // Replicate all selections across all tiles
  for (let col = 0; col < numColumns; col += 1) {
    for (let row = 0; row < numRows; row += 1) {
      const colOffset = (col - sourceTile.col) * xOffset
      const rowOffset = -(row - sourceTile.row) * yOffset
      
      // Replicate shapes
      shapesToUse.forEach((shape) => {
        const replicatedShape = transformShapeCoordinates(shape, colOffset, rowOffset)
        replicatedShapes.push(replicatedShape)
      })

      // Label visibility logic:
      // - Always show on source tile (where morphotype was drawn)
      // - If user is focused on a specific tile, also show on that tile
      // - Otherwise, hide labels to reduce clutter
      const isSourceTile = col === sourceTile.col && row === sourceTile.row
      const isFocusedTile = focusedTile ? (col === focusedTile.col && row === focusedTile.row) : false
      const shouldShowLabel = isSourceTile || isFocusedTile

      if (shouldShowLabel) {
        highestPoints.forEach((point) => {
          const replicatedAnnotation = {
            x: point.x + colOffset,
            y: point.y + rowOffset,
            ...getAnnotationStandardProps(pinnedMorphotype),
          } as Partial<Annotations>

          replicatedAnnotations.push(replicatedAnnotation)
        })
      }
    }
  }
  
  return {
    shapes: replicatedShapes,
    shapeAnnotations: replicatedAnnotations,
  }
}

// Since we always show all morphotypes for global selection behavior,
// this function is simplified to always return true
const shouldShowBasedOnComparisonDimensions = () => true

/**
 * Detects which tile (if any) the user is currently focused on in multi-plot mode
 * Returns undefined if viewing the full grid or if not in multi-plot mode
 */
const detectFocusedTile = (
  currentRange: { x1?: number; x2?: number; y1?: number; y2?: number } | undefined,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  numColumns: number,
  numRows: number
): { col: number; row: number } | undefined => {
  // No range means we're at initial zoom - show all labels
  if (!currentRange || currentRange.x1 === undefined || currentRange.x2 === undefined) {
    return undefined
  }

  const { minX, maxX, minY, maxY } = bounds
  const tileWidth = maxX - minX
  const tileHeight = maxY - minY
  const xOffset = tileWidth * TILE_OFFSET_MULTIPLIER
  const yOffset = tileHeight * TILE_OFFSET_MULTIPLIER

  // Calculate the full grid bounds (from first tile min to last tile max)
  const gridMinX = minX
  const gridMaxX = minX + tileWidth + (xOffset * (numColumns - 1))
  const gridMinY = minY - (yOffset * (numRows - 1))
  const gridMaxY = maxY

  const gridWidth = gridMaxX - gridMinX
  const gridHeight = gridMaxY - gridMinY

  const currentWidth = currentRange.x2 - currentRange.x1
  const currentHeight = (currentRange.y2 ?? maxY) - (currentRange.y1 ?? minY)

  // If viewing >= 90% of grid in both dimensions, consider it "viewing all"
  const viewingWidthRatio = currentWidth / gridWidth
  const viewingHeightRatio = currentHeight / gridHeight

  if (viewingWidthRatio >= 0.9 && viewingHeightRatio >= 0.9) {
    return undefined
  }

  // User is focused on a specific area - find which tile has most overlap
  let maxOverlap = 0
  let focusedTile: { col: number; row: number } | undefined

  for (let col = 0; col < numColumns; col += 1) {
    for (let row = 0; row < numRows; row += 1) {
      // Calculate this tile's bounds
      const tileMinX = minX + (xOffset * col)
      const tileMaxX = tileMinX + tileWidth
      const tileMaxY = maxY - (yOffset * row)
      const tileMinY = tileMaxY - tileHeight

      // Calculate overlap between current view and this tile
      const overlapMinX = Math.max(currentRange.x1, tileMinX)
      const overlapMaxX = Math.min(currentRange.x2, tileMaxX)
      const overlapMinY = Math.max(currentRange.y1 ?? minY, tileMinY)
      const overlapMaxY = Math.min(currentRange.y2 ?? maxY, tileMaxY)

      if (overlapMinX < overlapMaxX && overlapMinY < overlapMaxY) {
        const overlapArea = (overlapMaxX - overlapMinX) * (overlapMaxY - overlapMinY)
        if (overlapArea > maxOverlap) {
          maxOverlap = overlapArea
          focusedTile = { col, row }
        }
      }
    }
  }

  return focusedTile
}

interface GetMorphotypeSelectionInfoOptions {
  morphotypes?: PinnedMorphotype[]
  visibleDimensions?: CellInfoDimension[]
  plotMetadata?: { minX: number; minY: number; maxX: number; maxY: number }
  currentRange?: { x1?: number; x2?: number; y1?: number; y2?: number }
  cellInfo?: CellInfoWithFilterFlag[]
}

export const getMorphotypeSelectionInfo = ({
  morphotypes,
  visibleDimensions,
  plotMetadata,
  currentRange,
  cellInfo,
}: GetMorphotypeSelectionInfoOptions = {}): {
  /**
   * Add to Plot.layout to display the shapes that were selected
   */
  shapes: Partial<Shape>[]
  /**
   * Add to Plot.layout.annotations to show the labels of the shapes that were selected
   */
  shapeAnnotations: Partial<Annotations>[]
} => {
  if (!morphotypes)
    return {
      shapes: [],
      shapeAnnotations: [],
    }

  // Detect if user is focused on a specific tile in multi-plot mode
  const bounds = plotMetadata || { minX: -10, minY: -10, maxX: 10, maxY: 10 }
  const numColumns = visibleDimensions?.[0]?.values?.length || 1
  const numRows = visibleDimensions?.[1]?.values?.length || 1
  const isMultiPlotMode = visibleDimensions && visibleDimensions.length > 0
  const focusedTile = isMultiPlotMode
    ? detectFocusedTile(currentRange, bounds, numColumns, numRows)
    : undefined

  // Check for overlapping morphotypes to highlight them
  const overlappingPairs = checkForOverlappingMorphotypes(morphotypes)
  const overlappingMorphotypeIds = new Set<number>()
  overlappingPairs.forEach((pair: { morphotype1: PinnedMorphotype; morphotype2: PinnedMorphotype }) => {
    overlappingMorphotypeIds.add(pair.morphotype1.id)
    overlappingMorphotypeIds.add(pair.morphotype2.id)
  })

  const selectionInfo = morphotypes.flatMap((pinnedMorphotype, currentIndex) => {
    // Handle cluster-based morphotypes with dotted outlines
    if (pinnedMorphotype.isClusterBased) {
      // Only show outline if morphotype is active
      if (!pinnedMorphotype.active || !pinnedMorphotype.cells.points || pinnedMorphotype.cells.points.length < 3) {
        return []
      }

      // Calculate outlines for disconnected groups with barrier detection
      // This detects when other clusters physically separate groups
      // Use the cells already stored in the morphotype (from when it was created)
      // but filter out any that are currently prefiltered
      const targetClusterId = pinnedMorphotype.leidenCluster

      // Build a set of currently filtered cell IDs for fast lookup
      // Only filter cells if cellInfo provides isFiltered flags (for backward compatibility)
      // prefilteredCellIdSet is used elsewhere for prefilter mode, not for morphotype outlines
      const filteredCellIds = new Set<string>()
      if (cellInfo && cellInfo.length > 0) {
        cellInfo.forEach((cell) => {
          if (cell.isFiltered) {
            filteredCellIds.add(cell.CELL_ID)
          }
        })
      }

      // Convert stored points to the format expected by calculateClusterOutline
      // Filter out cells that are currently prefiltered
      const morphotypeCells = pinnedMorphotype.cells.points
        .filter((point) => {
          // Keep cells that have cellId and are not in the filtered set
          if (!point.cellId) return true // Keep cells without cellId (can't check if filtered)
          return !filteredCellIds.has(point.cellId) // Exclude prefiltered cells
        })
        .map((point) => ({
          x: point.x,
          y: point.y,
          clusterId: targetClusterId,
          isFiltered: false, // These cells passed the filter check
        }))

      // For barrier detection context, also include other cells from cellInfo if available
      // This allows the algorithm to detect when other clusters separate groups
      const allCellsWithClusters = cellInfo && cellInfo.length > 0
        ? [
            ...morphotypeCells,
            ...cellInfo
              .filter((cell) => cell.leiden !== targetClusterId) // Only other clusters for context
              .map((cell) => ({
                x: +cell.UMAP_0,
                y: +cell.UMAP_1,
                clusterId: cell.leiden,
                isFiltered: cell.isFiltered,
              })),
          ]
        : morphotypeCells

      // Calculate outlines with barrier detection
      // Will use the morphotype cells (with targetClusterId) as the target cluster
      const outlineResults = calculateClusterOutline(allCellsWithClusters, targetClusterId)

      if (outlineResults.length === 0) {
        return [] // Unable to calculate any outlines
      }

      // Determine fill color based on highlight state only
      // Note: Overlaps are detected and shown in dialog/train classifier, but don't change visual appearance
      const fillcolor = pinnedMorphotype.isHighlighted ? '#bbdefb' : 'transparent'
      const opacity = pinnedMorphotype.isHighlighted ? 0.1 : 1

      // Create dotted outline shapes for each disconnected group
      const clusterShapes: Partial<Shape>[] = outlineResults.map((result) => ({
        type: 'path',
        path: result.path,
        xref: 'x',
        yref: 'y',
        line: {
          color: '#000000', // Always black - overlaps don't change color
          width: 2,
          dash: 'dot', // Dotted line to distinguish from geometric morphotypes
        },
        fillcolor,
        opacity,
      }))

      // Extract highest points for annotation positioning
      const highestPoints = outlineResults.map((result) => getHighestPointFromPath(result.path))

      // In multi-plot mode, replicate shapes across tiles with label only on largest group
      // In single-plot mode, show annotation only on the largest group
      if (isMultiPlotMode) {
        // Cluster-based morphotypes created in multi-plot mode: replicate shapes across all tiles
        // Show label only on the largest group (first in sorted array) on source/focused tiles
        // Assume created on tile (0,0) by default
        const sourceTile = { col: 0, row: 0 }
        const largestGroupHighestPoint = highestPoints[0] // Only label the largest group
        return replicateAcrossTiles(
          clusterShapes,
          [largestGroupHighestPoint], // Pass only the largest group's highest point
          pinnedMorphotype,
          visibleDimensions,
          bounds,
          sourceTile,
          focusedTile
        )
      }

      // Single-plot mode: show annotation only on the largest group
      const largestGroupHighestPoint = highestPoints[0]
      const clusterAnnotation: Partial<Annotations> = {
        x: largestGroupHighestPoint.x,
        y: largestGroupHighestPoint.y,
        ...getAnnotationStandardProps(pinnedMorphotype),
      }

      return {
        shapes: clusterShapes,
        shapeAnnotations: [clusterAnnotation],
      }
    }

    // older sessions' version config won't have transposed cells so use regular cells
    const selectionData = pinnedMorphotype.transposedCells ?? pinnedMorphotype.cells ?? {}
    const { selections, lassoPoints, range } = selectionData

    // Check if morphotype has any geometry (selections, lassoPoints, or range)
    const hasGeometry = selections || lassoPoints || range

    if (
      !pinnedMorphotype.active ||
      !hasGeometry ||
      !shouldShowBasedOnComparisonDimensions()
    ) {
      return []
    }

    const isOverlapping = overlappingMorphotypeIds.has(pinnedMorphotype.id)
    const shapeProps = getShapeStandardProps(pinnedMorphotype, isOverlapping)

    // Convert lassoPoints/range to selections format if needed
    let selectionsToUse = selections
    if (!selectionsToUse && (lassoPoints || range)) {
      // Create selection from lassoPoints or range
      const poly = getPolygonFromPinnedMorphotype(pinnedMorphotype)
      if (poly && poly[0] && poly[0].length > 0) {
        // Convert polygon to path format
        const pathString = poly[0].map((point, i) => {
          const [x, y] = point
          return i === 0 ? `M${x},${y}` : `L${x},${y}`
        }).join('')
        selectionsToUse = [{
          type: 'path',
          path: pathString,
        }]
      }
    }

    if (!selectionsToUse) {
      return []
    }

    let shapesToUse = selectionsToUse.map(
      (x) => {
        const shape = {
          ...x,
          ...shapeProps,
        } as Partial<Shape>

        // Ensure lasso paths are properly closed
        if (shape.type === 'path' && shape.path) {
          shape.path = ensurePathClosed(shape.path as string)
        }

        return shape
      }
    )

    const matchingIndexes = morphotypes.flatMap((x, i) => (x.id === pinnedMorphotype.id ? i : []))

    if (matchingIndexes.length > 1) {
      // we only need to find the union of the shapes the first time
      if (matchingIndexes[0] === currentIndex) {
        // Check if these morphotypes were created in multi-plot mode
        const hasComparisonSnapshot = morphotypes[matchingIndexes[0]].comparisonDimensionsSnapshot

        // Get polygons and normalize coordinates if needed
        const polys: Poly[] = matchingIndexes.map((pcgIndex) => {
          let poly = getPolygonFromPinnedMorphotype(morphotypes[pcgIndex])

          // If morphotype was created in multi-plot mode, normalize coordinates to tile (0,0)
          if (hasComparisonSnapshot && plotMetadata) {
            const morphotype = morphotypes[pcgIndex]
            const { selections: morphotypeSelections } = morphotype.transposedCells ?? morphotype.cells ?? {}

            if (morphotypeSelections && morphotypeSelections.length > 0) {
              const sourceTile = detectSourceTile(morphotypeSelections[0], bounds)

              // Calculate offset to transform back to tile (0,0)
              const { minX, minY, maxX, maxY } = bounds
              const xOffset = (maxX - minX) * TILE_OFFSET_MULTIPLIER
              const yOffset = (maxY - minY) * TILE_OFFSET_MULTIPLIER
              const colOffset = -sourceTile.col * xOffset
              const rowOffset = sourceTile.row * yOffset

              // Transform polygon coordinates
              poly = poly.map(ring =>
                ring.map(point => [point[0] + colOffset, point[1] + rowOffset])
              ) as Poly
            }
          }

          return poly
        })

        // union returns ALL unions separated into groups
        const allUnionPolygons = union(polys[0], polys.slice(1))

        const paths = allUnionPolygons.map((unionPolygon) => {
          let path = ''
          unionPolygon[0].forEach((pair, i) => {
            path += `${i === 0 ? 'M' : 'L'}${pair[0]},${pair[1]}`
          })
          return path
        })

        shapesToUse = paths.map(
          (path) =>
            ({
              type: 'path',
              path: ensurePathClosed(path),
              ...shapeProps,
            } as Partial<Shape>)
        )
      } else {
        return []
      }
    }

    const highestPoints = shapesToUse.map((shape) => getHighestPointFromShapes(shape))

    // Always process morphotypes with comparisonDimensionsSnapshot first
    // This ensures selections created in comparison mode are visible in all contexts
    if (pinnedMorphotype.comparisonDimensionsSnapshot) {
      // If we're in comparison mode, replicate across all tiles
      if (visibleDimensions && visibleDimensions.length > 0) {
        const sourceTile = shapesToUse.length > 0 ? detectSourceTile(shapesToUse[0], bounds) : { col: 0, row: 0 }

        return replicateAcrossTiles(shapesToUse, highestPoints, pinnedMorphotype, visibleDimensions, bounds, sourceTile, focusedTile)
      }
      
      // We're in default mode, but this morphotype was created in comparison mode
      // Transform coordinates back to original plot system using the improved detection logic

      const sourceTile = shapesToUse.length > 0 ? detectSourceTile(shapesToUse[0], bounds) : { col: 0, row: 0 }
      
      // Calculate offset to transform back to tile (0,0)
      const { minX, minY, maxX, maxY } = bounds
      const xOffset = (maxX - minX) * TILE_OFFSET_MULTIPLIER
      const yOffset = (maxY - minY) * TILE_OFFSET_MULTIPLIER
      const colOffset = -sourceTile.col * xOffset
      const rowOffset = sourceTile.row * yOffset
      
      const transformedShapes = shapesToUse.map(shape => 
        transformShapeCoordinates(shape, colOffset, rowOffset)
      )
      
      // Transform annotations to match the transformed shapes
      const transformedAnnotations = highestPoints.map((highestPoint) => ({
        x: highestPoint.x + colOffset,
        y: highestPoint.y + rowOffset,
        ...getAnnotationStandardProps(pinnedMorphotype),
      } as Partial<Annotations>))
      
      return {
        shapes: transformedShapes,
        shapeAnnotations: transformedAnnotations,
      }
      
    }

    // Regular morphotypes (not created in comparison mode)
    // If we're in comparison mode, replicate across all tiles
    if (visibleDimensions && visibleDimensions.length > 0) {
      // For regular morphotypes, assume they were created on tile (0,0) and replicate from there
      const sourceTile = { col: 0, row: 0 }

      return replicateAcrossTiles(shapesToUse, highestPoints, pinnedMorphotype, visibleDimensions, bounds, sourceTile, focusedTile)
    }

    // Regular morphotype in default mode - return as-is
    return {
      shapes: shapesToUse,
      shapeAnnotations: highestPoints.map(
        (highestPoint) =>
          ({
            x: highestPoint.x,
            y: highestPoint.y,
            ...getAnnotationStandardProps(pinnedMorphotype),
          } as Partial<Annotations>)
      ),
    }
  })

  return {
    shapes: selectionInfo.flatMap((x) => x.shapes),
    shapeAnnotations: selectionInfo.flatMap((x) => x.shapeAnnotations),
  } as const
}

export default getMorphotypeSelectionInfo
